import React, { useState, useRef } from 'react';
import { Merge, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const MergePDF = () => {
    const [files, setFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const fileInputRef = useRef(null);

    const handleFilesSelected = (selectedFiles) => {
        if (selectedFiles && selectedFiles.length > 0) {
            const pdfFiles = Array.from(selectedFiles).filter(f => f.type === 'application/pdf');
            if (pdfFiles.length > 0) {
                setFiles(prev => [...prev, ...pdfFiles]);
                toast.success(`Added ${pdfFiles.length} file(s)`);
            } else {
                toast.error('Please select PDF files');
            }
        }
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const moveFile = (index, direction) => {
        if (direction === 'up' && index > 0) {
            setFiles(prev => {
                const newFiles = [...prev];
                [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
                return newFiles;
            });
        } else if (direction === 'down' && index < files.length - 1) {
            setFiles(prev => {
                const newFiles = [...prev];
                [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
                return newFiles;
            });
        }
    };

    const sortFiles = (sortType) => {
        setFiles(prev => {
            const sorted = [...prev];
            switch (sortType) {
                case 'name-asc':
                    return sorted.sort((a, b) => a.name.localeCompare(b.name));
                case 'name-desc':
                    return sorted.sort((a, b) => b.name.localeCompare(a.name));
                default:
                    return sorted;
            }
        });
        toast.success('Files sorted successfully');
    };

    const mergePDFs = async () => {
        if (files.length < 2) {
            toast.error('Please select at least 2 PDF files to merge');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);
            const mergedPdf = await PDFDocument.create();

            for (let i = 0; i < files.length; i++) {
                setProgress((i / files.length) * 90);
                const file = files[i];
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
                pages.forEach((page) => {
                    mergedPdf.addPage(page);
                });
            }

            setProgress(95);
            const pdfBytes = await mergedPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const filename = `merged-${new Date().toISOString().slice(0, 10)}.pdf`;

            setPreviewBlob(blob);
            setPreviewFilename(filename);
            setProgress(100);
            toast.success('PDFs merged successfully!');
        } catch (error) {
            console.error('Merge error:', error);
            toast.error(`Merge failed: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    // Trigger file input - use the same input as FileUploadZone
                    if (fileInputRef.current) {
                        fileInputRef.current.click();
                    }
                }}
                onSort={(sortType) => {
                    if (sortType && files.length > 0) {
                        sortFiles(sortType);
                    }
                }}
                notificationCount={files.length}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Merge size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Merge PDF
                </h1>
                <p className="tool-description">
                    Combine multiple PDF files into one. Drag and drop files to reorder them.
                </p>
            </div>

            {files.length === 0 ? (
                <FileUploadZone
                    onFilesSelected={handleFilesSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={true}
                    title="Drop PDF files here"
                    description="or click to browse (Select multiple PDFs)"
                    inputRef={fileInputRef}
                />
            ) : (
                <>
                    <Alert variant="info">
                        {files.length} file(s) selected. Files will be merged in the order shown below.
                    </Alert>

                    <Card>
                        <h3 style={{
                            fontSize: 'var(--text-lg)',
                            fontWeight: 'var(--font-bold)',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            Files to Merge
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                            {files.map((file, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--spacing-3)',
                                        padding: 'var(--spacing-3)',
                                        backgroundColor: 'var(--color-surface-elevated)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}
                                >
                                    <Badge variant="info">{index + 1}</Badge>
                                    <FileText size={20} />
                                    <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>{file.name}</span>
                                    <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => moveFile(index, 'up')}
                                            disabled={index === 0}
                                        >
                                            ↑
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => moveFile(index, 'down')}
                                            disabled={index === files.length - 1}
                                        >
                                            ↓
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeFile(index)}
                                        >
                                            ×
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <div className="button-group" style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                        <Button
                            variant="ghost"
                            size="md"
                            onClick={() => setFiles([])}
                        >
                            Clear All
                        </Button>
                        <FileUploadZone
                            onFilesSelected={handleFilesSelected}
                            acceptedTypes={['application/pdf']}
                            multiple={true}
                            inputRef={fileInputRef}
                            style={{ flex: 1 }}
                        >
                            <Button variant="secondary" size="md" style={{ width: '100%' }}>
                                Add More Files
                            </Button>
                        </FileUploadZone>
                    </div>

                    {isProcessing && (
                        <ProgressBar value={progress} striped showLabel />
                    )}

                    <div className="button-group">
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={mergePDFs}
                            disabled={isProcessing || files.length < 2}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Merge size={20} />
                            Merge {files.length} PDF{files.length !== 1 ? 's' : ''}
                        </Button>
                    </div>
                </>
            )}

            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default MergePDF;

